(() => {
  const variantId = document.currentScript?.dataset.variantId;
  if (!variantId) return;
  const root = window.Shopify?.routes?.root || '/';
  let pending = false;
  let cartWork = Promise.resolve();
  const busyControls = new Set();
  const buySelector = 'button.main-product-atc, button[id^="SectionAtcBtn-"]';

  async function cartRequest(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const cart = await response.json();
    if (!response.ok) throw new Error('Cart request failed');
    return cart;
  }

  function enqueue(task) {
    const work = cartWork.catch(() => {}).then(task);
    cartWork = work;
    return work;
  }

  // update.js sets an absolute quantity, including when the variant is absent.
  // Repeated clicks therefore cannot increment the quantity like add.js does.
  async function setSingleItem(addIfEmpty) {
    const cart = await cartRequest('cart.js');
    const keep = cart.items.find(item => String(item.variant_id) === variantId) ||
      (addIfEmpty ? null : cart.items[0]);
    if (!addIfEmpty && cart.item_count <= 1) return { cart, changed: false };
    const updates = {};
    cart.items.forEach(item => { updates[item.key] = item === keep ? 1 : 0; });
    if (!keep && addIfEmpty) updates[variantId] = 1;
    const updated = await cartRequest('cart/update.js', { updates });
    if (updated.item_count !== 1) throw new Error('Cart quantity could not be set');
    return { cart: updated, changed: true };
  }

  function resetControls() {
    pending = false;
    busyControls.forEach(control => {
      control.removeAttribute('aria-busy');
      control.removeAttribute('aria-disabled');
      if (control instanceof HTMLButtonElement) control.disabled = false;
    });
    busyControls.clear();
  }

  async function checkout(control) {
    if (pending) return;
    pending = true;
    document.querySelectorAll('[data-chromelie-checkout-error]').forEach(node => node.remove());
    if (control) {
      busyControls.add(control);
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
      if (control instanceof HTMLButtonElement) control.disabled = true;
    }
    try {
      await enqueue(() => setSingleItem(true));
      window.location.assign(root + 'checkout');
    } catch (error) {
      resetControls();
      const message = document.createElement('p');
      message.dataset.chromelieCheckoutError = '';
      message.setAttribute('role', 'alert');
      message.textContent = 'We couldn’t open checkout. Please try again.';
      if (control) control.insertAdjacentElement('afterend', message);
    }
  }

  function isAnalysisForm(form) {
    return form instanceof HTMLFormElement &&
      new URL(form.action, location.href).pathname.replace(/\/$/, '').endsWith('/cart/add') &&
      String(new FormData(form).get('id')) === variantId;
  }

  function stop(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // Capture before Shrine's product-form and sticky-button handlers add an item.
  window.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(buySelector);
    if (button) {
      const form = button.form || document.querySelector('product-form[data-main="true"] form');
      if (isAnalysisForm(form) && !button.disabled) {
        stop(event);
        checkout(button);
      }
      return;
    }
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname === root + 'checkout' ||
        url.pathname === root + 'cart/' + variantId + ':1') {
      stop(event);
      checkout(link);
    }
  }, true);

  window.addEventListener('submit', event => {
    if (isAnalysisForm(event.target)) {
      stop(event);
      checkout(event.submitter || event.target.querySelector('[type="submit"]'));
    } else if (event.submitter?.name === 'checkout') {
      stop(event);
      checkout(event.submitter);
    }
  }, true);

  function capQuantityInputs() {
    document.querySelectorAll('input[name="quantity"], input[name="updates[]"]').forEach(input => {
      input.max = '1';
      if (Number(input.value) > 1) input.value = '1';
      const plus = input.closest('quantity-input')?.querySelector('button[name="plus"]');
      if (plus) plus.disabled = true;
    });
  }

  window.addEventListener('change', event => {
    if (event.target instanceof HTMLInputElement &&
        event.target.matches('input[name="quantity"], input[name="updates[]"]') &&
        Number(event.target.value) > 1) event.target.value = '1';
  }, true);

  // Also repair older carts on a fresh visit or browser Back, without adding
  // anything to an empty cart. Back/forward cache can retain disabled buttons.
  window.addEventListener('pageshow', () => {
    resetControls();
    capQuantityInputs();
    enqueue(() => setSingleItem(false)).then(result => {
      if (result.changed && location.pathname.replace(/\/$/, '') === root + 'cart') location.reload();
    }).catch(() => {});
  });
  document.addEventListener('shopify:section:load', capQuantityInputs);
})();
